import { forwardRef, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, BaseDropdownContainerUI, Button, type ButtonProps, ICON_COLOR } from '@zep-us/zus';
import { CheckSmallIcon, MinusSmallIcon, PlusSmallIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
import { type TreeMenuItem } from '@/views/build/features/category-tree';
interface ColumnHeaderProps {
  label: string;
  required?: boolean;
  error?: string;
}
const ColumnHeader = ({
  label,
  required,
  error
}: ColumnHeaderProps) => {
  return <div className="flex items-center justify-between p-sm py-xs" data-sentry-component="ColumnHeader" data-sentry-source-file="ColumnUI.tsx">
      <span className={cn('text-body-lg-bold text-default', error ? 'text-destructive' : 'text-default')}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
    </div>;
};
const ColumnDivider = () => {
  return <div className="h-full w-0 border-x-[0.5px] border-alternative" data-sentry-component="ColumnDivider" data-sentry-source-file="ColumnUI.tsx" />;
};
const ColumnSelectButton = forwardRef(({
  isSelected,
  className,
  children,
  rightSlot,
  ...props
}: Omit<ButtonProps, 'asChild'> & {
  isSelected: boolean;
  rightSlot?: React.ReactNode;
}, _ref) => {
  return <Button size="lg" variant="transparent" intent={isSelected ? 'alternative' : 'neutral'} rightSlot={rightSlot ?? (isSelected ? <CheckSmallIcon size="sm" /> : <></>)} className={cn('w-full text-body-lg-semibold text-neutral justify-between', isSelected && 'text-strong bg-opacity-violet-1', className)} {...props}>
        {children}
      </Button>;
});
const ColumnSelectDropdown = ({
  triggerLabel,
  triggerClassName,
  options,
  onSelect,
  onClick,
  value,
  isOpenForced,
  onTriggerMouseEnter,
  onTriggerMouseLeave,
  onSubMouseEnter,
  onSubMouseLeave
}: {
  triggerLabel: string;
  triggerClassName?: string;
  options: TreeMenuItem[];
  onClick: () => void;
  onSelect: (value: string) => void;
  value: string;
  isOpenForced?: boolean;
  onTriggerMouseEnter?: () => void;
  onTriggerMouseLeave?: () => void;
  onSubMouseEnter?: (value: string, label: string) => void;
  onSubMouseLeave?: () => void;
}) => {
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const isOpen = isOpenForced || accordionValue === 'item-1';
  return <Accordion type="single" collapsible value={isOpenForced ? 'item-1' : accordionValue} onValueChange={setAccordionValue} data-sentry-element="Accordion" data-sentry-component="ColumnSelectDropdown" data-sentry-source-file="ColumnUI.tsx">
      <AccordionItem value="item-1" className="border-none" data-sentry-element="AccordionItem" data-sentry-source-file="ColumnUI.tsx">
        <AccordionTrigger asChild className="p-0 hover:no-underline" data-sentry-element="AccordionTrigger" data-sentry-source-file="ColumnUI.tsx">
          <Button onClick={onClick} onMouseEnter={onTriggerMouseEnter} onMouseLeave={onTriggerMouseLeave} size="lg" variant="transparent" intent="neutral" rightSlot={<OpenIndicatorIcon isOpen={isOpen} />} className={cn('w-full justify-between text-body-lg-semibold text-neutral', triggerClassName)} data-sentry-element="Button" data-sentry-source-file="ColumnUI.tsx">
            {triggerLabel}
          </Button>
        </AccordionTrigger>
        <AccordionContent asChild data-sentry-element="AccordionContent" data-sentry-source-file="ColumnUI.tsx">
          <BaseDropdownContainerUI className="px-xs pb-3xs pt-xs" data-sentry-element="BaseDropdownContainerUI" data-sentry-source-file="ColumnUI.tsx">
            {options.map(option => <ColumnSelectButton key={option.value} isSelected={value === option.value} onClick={() => {
            onSelect(option.value);
          }} onMouseEnter={() => onSubMouseEnter?.(option.value, option.label as string)} onMouseLeave={() => onSubMouseLeave?.()}>
                {option.label}
              </ColumnSelectButton>)}
          </BaseDropdownContainerUI>
        </AccordionContent>
      </AccordionItem>
    </Accordion>;
};
const OpenIndicatorIcon = ({
  isOpen
}: {
  isOpen: boolean;
}) => {
  return <>
      <PlusSmallIcon size="sm" fill={ICON_COLOR.neutral} className={cn(isOpen && 'hidden')} data-sentry-element="PlusSmallIcon" data-sentry-source-file="ColumnUI.tsx" />
      <MinusSmallIcon size="sm" fill={ICON_COLOR.neutral} className={cn(isOpen && 'block', !isOpen && 'hidden')} data-sentry-element="MinusSmallIcon" data-sentry-source-file="ColumnUI.tsx" />
    </>;
};
export const ColumnUI = {
  Header: ColumnHeader,
  Divider: ColumnDivider,
  SelectButton: ColumnSelectButton,
  SelectDropdown: ColumnSelectDropdown
};